import { useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  Infinity as InfinityIcon,
} from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import api from "../../lib/api";
import { DiscountsSection } from "./DiscountsSection";

export interface PackageRow {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  monthlyPrice: number | string;
  yearlyPrice: number | string | null;
  maxInvoices: number | null;
  maxQuotes: number | null;
  maxExpenses: number | null;
  maxContacts: number | null;
  maxUsers: number | null;
  freeLayoutTemplates: number | null;
  hasOcr: boolean;
  hasVatReport: boolean;
  hasBankLink: boolean;
  hasQuotes: boolean;
  hasExpenses: boolean;
  hasPayments: boolean;
  hasKasboek: boolean;
  ocrCreditsPerMonth: number | null;
  isActive: boolean;
  sortOrder: number;
  _count?: { subscriptions: number };
}

interface ModulesTabProps {
  packages: PackageRow[];
  reload: () => Promise<void>;
}

type ModalState =
  | { type: "create" }
  | { type: "edit"; pkg: PackageRow }
  | { type: "delete"; pkg: PackageRow }
  | null;

export function ModulesTab({ packages, reload }: ModulesTabProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const sorted = [...packages].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name)
  );

  const toggleStatus = async (pkg: PackageRow) => {
    try {
      await api.patch(`/api/superadmin/packages/${pkg.id}/status`, { isActive: !pkg.isActive });
      await reload();
      flash(`Pakket ${pkg.isActive ? "gedeactiveerd" : "geactiveerd"}.`);
    } catch (err: any) {
      flash(err?.response?.data?.error || "Actie mislukt");
    }
  };

  const move = async (pkg: PackageRow, dir: -1 | 1) => {
    const idx = sorted.findIndex((p) => p.id === pkg.id);
    const swapWith = sorted[idx + dir];
    if (!swapWith) return;
    try {
      await api.patch("/api/superadmin/packages/reorder", [
        { id: pkg.id, sortOrder: swapWith.sortOrder },
        { id: swapWith.id, sortOrder: pkg.sortOrder },
      ]);
      await reload();
    } catch (err: any) {
      flash(err?.response?.data?.error || "Volgorde wijzigen mislukt");
    }
  };

  return (
    <div className="space-y-6">
      {toast && (
        <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2.5 text-sm font-semibold">
          <CheckCircle2 size={24} /> {toast}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-800">Pakketten (modules)</h3>
          <Button size="sm" onClick={() => setModal({ type: "create" })}>
            <Plus size={24} /> Nieuw pakket
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-slate-600 w-16">Volgorde</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Pakket</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Maand</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Jaar</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Limieten</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Abonnementen</th>
                <th className="text-left px-4 py-3 font-bold text-slate-600">Status</th>
                <th className="text-right px-4 py-3 font-bold text-slate-600">Acties</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((pkg, i) => (
                <tr
                  key={pkg.id}
                  className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${
                    !pkg.isActive ? "opacity-50" : ""
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => move(pkg, -1)}
                        disabled={i === 0}
                        className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"
                      >
                        <ArrowUp size={21} />
                      </button>
                      <button
                        onClick={() => move(pkg, 1)}
                        disabled={i === sorted.length - 1}
                        className="p-1 rounded hover:bg-slate-200 disabled:opacity-30"
                      >
                        <ArrowDown size={21} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-800">{pkg.name}</p>
                    <p className="text-xs text-slate-400 font-mono">{pkg.key}</p>
                    {pkg.description && (
                      <p className="text-xs text-slate-500 mt-0.5 max-w-xs truncate">
                        {pkg.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold">€{Number(pkg.monthlyPrice).toFixed(2)}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {pkg.yearlyPrice ? `€${Number(pkg.yearlyPrice).toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {[
                      `${fmt(pkg.maxInvoices)} fact.`,
                      `${fmt(pkg.maxQuotes)} off.`,
                      `${fmt(pkg.maxContacts)} cont.`,
                    ].join(" · ")}
                  </td>
                  <td className="px-4 py-3">
                    <Badge status={pkg._count?.subscriptions ? "SENT" : "default"}>
                      {pkg._count?.subscriptions || 0}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleStatus(pkg)}
                      className={`px-2.5 py-0.5 rounded-full text-xs font-bold border transition-colors ${
                        pkg.isActive
                          ? "bg-emerald-100/80 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-500 border-slate-200"
                      }`}
                    >
                      {pkg.isActive ? "Actief" : "Inactief"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setModal({ type: "edit", pkg })}
                        className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600 transition-colors"
                        title="Bewerken"
                      >
                        <Pencil size={24} />
                      </button>
                      <button
                        onClick={() => setModal({ type: "delete", pkg })}
                        className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                        title="Verwijderen"
                      >
                        <Trash2 size={24} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sorted.length === 0 && (
          <p className="text-slate-500 text-center py-8">Geen pakketten.</p>
        )}
      </Card>

      <DiscountsSection packages={packages} flash={flash} />

      {modal?.type === "create" && (
        <PackageFormModal
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "edit" && (
        <PackageFormModal
          pkg={modal.pkg}
          onClose={() => setModal(null)}
          onSaved={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
      {modal?.type === "delete" && (
        <DeletePackageModal
          pkg={modal.pkg}
          onClose={() => setModal(null)}
          onDone={async (msg) => {
            await reload();
            setModal(null);
            flash(msg);
          }}
        />
      )}
    </div>
  );
}

function fmt(n: number | null | undefined) {
  if (n === null || n === undefined) return "∞";
  return n.toString();
}

interface PackageFormModalProps {
  pkg?: PackageRow;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
}

function PackageFormModal({ pkg, onClose, onSaved }: PackageFormModalProps) {
  const isEdit = !!pkg;
  const [f, setF] = useState({
    key: pkg?.key || "",
    name: pkg?.name || "",
    description: pkg?.description || "",
    monthlyPrice: pkg ? String(pkg.monthlyPrice) : "",
    yearlyPrice: pkg?.yearlyPrice ? String(pkg.yearlyPrice) : "",
    maxInvoices: pkg?.maxInvoices == null ? "" : String(pkg.maxInvoices),
    maxQuotes: pkg?.maxQuotes == null ? "" : String(pkg.maxQuotes),
    maxExpenses: pkg?.maxExpenses == null ? "" : String(pkg.maxExpenses),
    maxContacts: pkg?.maxContacts == null ? "" : String(pkg.maxContacts),
    maxUsers: pkg?.maxUsers == null ? "" : String(pkg.maxUsers),
    freeLayoutTemplates: pkg?.freeLayoutTemplates == null ? "" : String(pkg.freeLayoutTemplates),
    hasOcr: pkg?.hasOcr || false,
    hasVatReport: pkg?.hasVatReport || false,
    hasBankLink: pkg?.hasBankLink || false,
    hasQuotes: pkg?.hasQuotes ?? true,
    hasExpenses: pkg?.hasExpenses ?? true,
    hasPayments: pkg?.hasPayments ?? true,
    hasKasboek: pkg?.hasKasboek ?? true,
    ocrCreditsPerMonth: pkg?.ocrCreditsPerMonth == null ? "" : String(pkg.ocrCreditsPerMonth),
    sortOrder: pkg ? String(pkg.sortOrder) : "0",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  // empty string → null (unlimited); number otherwise
  const numOrNull = (v: string) => (v === "" ? null : Number(v));

  const submit = async () => {
    setError("");
    if (!f.name.trim()) return setError("Naam is verplicht");
    if (!isEdit && !f.key.trim()) return setError("Key is verplicht");
    if (f.monthlyPrice === "" || Number(f.monthlyPrice) < 0)
      return setError("Maandprijs is verplicht");

    const body: any = {
      name: f.name,
      description: f.description || null,
      monthlyPrice: Number(f.monthlyPrice),
      yearlyPrice: f.yearlyPrice === "" ? null : Number(f.yearlyPrice),
      maxInvoices: numOrNull(f.maxInvoices),
      maxQuotes: numOrNull(f.maxQuotes),
      maxExpenses: numOrNull(f.maxExpenses),
      maxContacts: numOrNull(f.maxContacts),
      maxUsers: numOrNull(f.maxUsers),
      freeLayoutTemplates: numOrNull(f.freeLayoutTemplates),
      hasOcr: f.hasOcr,
      hasVatReport: f.hasVatReport,
      hasBankLink: f.hasBankLink,
      hasQuotes: f.hasQuotes,
      hasExpenses: f.hasExpenses,
      hasPayments: f.hasPayments,
      hasKasboek: f.hasKasboek,
      ocrCreditsPerMonth: f.ocrCreditsPerMonth === "" ? 0 : Number(f.ocrCreditsPerMonth),
      sortOrder: Number(f.sortOrder || 0),
    };
    if (!isEdit) body.key = f.key;

    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/api/superadmin/packages/${pkg!.id}`, body);
        await onSaved("Pakket opgeslagen.");
      } else {
        await api.post("/api/superadmin/packages", body);
        await onSaved("Pakket aangemaakt.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  const limitFields: { key: keyof typeof f; label: string }[] = [
    { key: "maxInvoices", label: "Max. facturen/maand (leeg = ∞)" },
    { key: "maxQuotes", label: "Max. offertes/maand (leeg = ∞)" },
    { key: "maxExpenses", label: "Max. uitgaven/maand (leeg = ∞)" },
    { key: "maxContacts", label: "Max. contacten (leeg = ∞)" },
    { key: "maxUsers", label: "Max. gebruikers (leeg = ∞)" },
    { key: "freeLayoutTemplates", label: "Gratis lay-outtemplates (leeg = ∞)" },
  ];

  const toggles: { key: keyof typeof f; label: string }[] = [
    { key: "hasOcr", label: "Bonnetjes lezen" },
    { key: "hasVatReport", label: "BTW-overzicht" },
    { key: "hasBankLink", label: "Bank-koppeling" },
  ];

  // Feature-gates: bepalen welke sidebar-pagina's zichtbaar zijn voor bedrijven
  // op dit pakket. Zonder offerte geen factuur, dus hasQuotes staat standaard aan.
  const featureGates: { key: keyof typeof f; label: string }[] = [
    { key: "hasQuotes", label: "Offertes" },
    { key: "hasExpenses", label: "Uitgaven" },
    { key: "hasPayments", label: "Inkomsten (betalingen)" },
    { key: "hasKasboek", label: "Kasboek" },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Pakket bewerken" : "Nieuw pakket"}
      size="xl"
      footer={
        <div className="flex justify-between items-center">
          {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={submit} loading={loading}>
              {isEdit ? "Opslaan" : "Aanmaken"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid md:grid-cols-2 gap-4">
        {!isEdit && (
          <Input
            label="Key (kleine letters/cijfers/streepje)"
            value={f.key}
            onChange={set("key")}
            placeholder="bijv. professional"
          />
        )}
        <Input label="Naam" value={f.name} onChange={set("name")} />
        <div className="md:col-span-2">
          <Input label="Omschrijving" value={f.description} onChange={set("description")} />
        </div>
        <Input
          label="Maandprijs (€)"
          type="number"
          step="0.01"
          value={f.monthlyPrice}
          onChange={set("monthlyPrice")}
        />
        <Input
          label="Jaarprijs (€, leeg = maand×12×0.95)"
          type="number"
          step="0.01"
          value={f.yearlyPrice}
          onChange={set("yearlyPrice")}
        />
        <Input
          label="Volgorde"
          type="number"
          value={f.sortOrder}
          onChange={set("sortOrder")}
        />
        <Input
          label="Uitgaven-bon/maand lezen"
          type="number"
          value={f.ocrCreditsPerMonth}
          onChange={set("ocrCreditsPerMonth")}
        />

        <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {limitFields.map((lf) => (
            <Input
              key={lf.key}
              label={lf.label}
              type="number"
              value={f[lf.key] as string}
              onChange={set(lf.key)}
            />
          ))}
        </div>

        <div className="md:col-span-2 pt-2 border-t border-slate-100">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
            Modules (zichtbare pagina&apos;s)
          </p>
          <div className="flex flex-wrap gap-4">
            {featureGates.map((t) => (
              <label
                key={t.key}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={f[t.key] as boolean}
                  onChange={set(t.key)}
                  className="w-4 h-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
                />
                {t.label}
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Uit-vinken verbergt de pagina in de sidebar voor bedrijven op dit pakket.
          </p>
        </div>

        <div className="md:col-span-2 flex flex-wrap gap-4 pt-2 border-t border-slate-100">
          {toggles.map((t) => (
            <label
              key={t.key}
              className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={f[t.key] as boolean}
                onChange={set(t.key)}
                className="w-4 h-4 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
              />
              {t.label}
            </label>
          ))}
          <span className="flex items-center gap-1 text-xs text-slate-400 ml-auto">
            <InfinityIcon size={21} /> leeg = onbeperkt
          </span>
        </div>
      </div>
    </Modal>
  );
}

function DeletePackageModal({
  pkg,
  onClose,
  onDone,
}: {
  pkg: PackageRow;
  onClose: () => void;
  onDone: (msg: string) => Promise<void> | void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const del = async () => {
    setLoading(true);
    setError("");
    try {
      await api.delete(`/api/superadmin/packages/${pkg.id}`);
      await onDone("Pakket verwijderd.");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Verwijderen mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Pakket verwijderen"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Annuleren
          </Button>
          <Button variant="danger" onClick={del} loading={loading}>
            Verwijderen
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Weet je zeker dat je pakket <strong>{pkg.name}</strong> wilt verwijderen?
        </p>
        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}